import React, { useState } from "react";
import classNames from "classnames";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { FinanceStore } from "./finance-store";
import { MODULES, AGENCES, CHANNELS, STATEMENT_SOURCES, PeriodKey, PERIOD_LABELS, periodRange, inRange, money, fDay, downloadCsv, todayIso, addDaysIso, Transaction } from "./finance-data";
import { FinPage, Card, PeriodPicker, btnPrimary, Toast } from "./finance-ui";

type Cell = string | number;
type Report = {
  id: string;
  title: string;
  desc: string;
  columns: string[];
  // Numeric columns (index) formatted as amounts and summed in the total row.
  moneyCols: number[];
  build: (rows: Transaction[], store: FinanceStore, range: [string, string]) => Cell[][];
};

const sum = (a: Transaction[], f: (t: Transaction) => number = (t) => t.amount) => a.reduce((x, t) => x + f(t), 0);

const REPORTS: Report[] = [
  {
    id: "synthese",
    title: "Synthèse financière (état final)",
    desc: "Encaissements, sorties, frais et net par activité sur la période.",
    columns: ["Activité", "Encaissements", "Remboursements", "Reversements CR", "Frais opérateurs", "Net"],
    moneyCols: [1, 2, 3, 4, 5],
    build: (rows) =>
      MODULES.map((m) => {
        const r = rows.filter((t) => t.module === m && t.status === "Réussie");
        const inn = sum(r.filter((t) => t.amount > 0));
        const ref = sum(r.filter((t) => t.type === "Remboursement"));
        const rev = sum(r.filter((t) => t.type === "Reversement CR"));
        const fee = sum(r, (t) => t.fee);
        return [m, inn, ref, rev, -fee, inn + ref + rev - fee];
      }),
  },
  {
    id: "journal",
    title: "Journal des encaissements",
    desc: "Toutes les opérations réussies, chronologiquement.",
    columns: ["Date", "Référence", "Client", "Activité", "Canal", "Réf. opérateur", "Montant"],
    moneyCols: [6],
    build: (rows) =>
      rows
        .filter((t) => t.status === "Réussie")
        .sort((a, b) => a.date.localeCompare(b.date))
        .map((t) => [t.date.replace("T", " "), t.ref, t.client, t.module, t.channel, t.opRef || "—", t.amount]),
  },
  {
    id: "caisse",
    title: "État de caisse par agence",
    desc: "Entrées et sorties par agence et par canal de paiement.",
    columns: ["Agence", "Canal", "Opérations", "Entrées", "Sorties", "Solde"],
    moneyCols: [3, 4, 5],
    build: (rows) =>
      AGENCES.flatMap((a) =>
        CHANNELS.map((c) => {
          const r = rows.filter((t) => t.agence === a && t.channel === c && t.status === "Réussie");
          const inn = sum(r.filter((t) => t.amount > 0));
          const out = sum(r.filter((t) => t.amount < 0));
          return [a, c, r.length, inn, out, inn + out] as Cell[];
        }).filter((x) => x[2] !== 0)
      ),
  },
  {
    id: "ca",
    title: "Chiffre d'affaires par activité et par jour",
    desc: "CA encaissé quotidien pour chaque activité.",
    columns: ["Jour", ...MODULES, "Total"],
    moneyCols: [1, 2, 3, 4],
    build: (rows, _s, [a, b]) => {
      const out: Cell[][] = [];
      for (let d = a; d <= b; d = addDaysIso(d, 1)) {
        const day = rows.filter((t) => t.date.startsWith(d) && t.status === "Réussie" && t.amount > 0);
        const vals = MODULES.map((m) => sum(day.filter((t) => t.module === m)));
        if (vals.some((v) => v)) out.push([fDay(d), ...vals, vals.reduce((x, y) => x + y, 0)]);
      }
      return out;
    },
  },
  {
    id: "sorties",
    title: "Remboursements & reversements",
    desc: "Remboursements clients et reversements de contre-remboursement.",
    columns: ["Date", "Référence", "Type", "Client", "Dossier", "Canal", "Montant"],
    moneyCols: [6],
    build: (rows) => rows.filter((t) => t.amount < 0 && t.status === "Réussie").map((t) => [t.date.replace("T", " "), t.ref, t.type, t.client, t.dossier, t.channel, t.amount]),
  },
  {
    id: "frais",
    title: "Frais opérateurs",
    desc: "Volume traité, frais prélevés et taux effectif par canal.",
    columns: ["Canal", "Opérations", "Volume", "Frais", "Taux effectif"],
    moneyCols: [2, 3],
    build: (rows) =>
      CHANNELS.map((c) => {
        const r = rows.filter((t) => t.channel === c && t.status === "Réussie");
        const vol = sum(r, (t) => Math.abs(t.amount));
        const fee = sum(r, (t) => t.fee);
        return [c, r.length, vol, fee, vol ? `${((fee / vol) * 100).toFixed(2)} %` : "—"];
      }),
  },
  {
    id: "rapprochement",
    title: "État de rapprochement",
    desc: "Situation du rapprochement des relevés par opérateur.",
    columns: ["Source", "Lignes relevé", "Rapprochées", "Justifiées / litiges", "À traiter", "Montant à traiter"],
    moneyCols: [5],
    build: (_rows, s, range) =>
      STATEMENT_SOURCES.map((src) => {
        const lines = s.statement.filter((l) => l.source === src && inRange(l.date, range));
        const rec = lines.filter((l) => s.links[l.id]);
        const noted = lines.filter((l) => !s.links[l.id] && s.notes[l.id]);
        const open = lines.filter((l) => !s.links[l.id] && !s.notes[l.id]);
        return [src, lines.length, rec.length, noted.length, open.length, open.reduce((a, l) => a + l.amount, 0)];
      }),
  },
  {
    id: "commissions",
    title: "Commissions commerciaux",
    desc: "Encaissements par agent et commission de 5 %.",
    columns: ["Agent", "Opérations", "Encaissé", "Commission 5 %"],
    moneyCols: [2, 3],
    build: (rows) => {
      const agents = Array.from(new Set(rows.map((t) => t.agent))).sort();
      return agents.map((a) => {
        const r = rows.filter((t) => t.agent === a && t.status === "Réussie" && t.amount > 0);
        const inn = sum(r);
        return [a, r.length, inn, Math.round(inn * 0.05)];
      });
    },
  },
];

type Edition = { id: string; report: string; period: string; format: string; at: string; rows: number };

export default function FinanceEtats({ store }: { store: FinanceStore }) {
  const [reportId, setReportId] = useState(REPORTS[0].id);
  const [period, setPeriod] = useState<PeriodKey>("month");
  const [from, setFrom] = useState(addDaysIso(todayIso(), -29));
  const [to, setTo] = useState(todayIso());
  const [agence, setAgence] = useState("Toutes");
  const [module, setModule] = useState("Toutes");
  const [format, setFormat] = useState<"PDF" | "Excel" | "CSV">("Excel");
  const [history, setHistory] = useState<Edition[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const flash = (m: string) => {
    setToast(m);
    window.setTimeout(() => setToast(null), 2600);
  };

  const report = REPORTS.find((r) => r.id === reportId)!;
  const range = periodRange(period, from, to);
  const scoped = store.txs.filter((t) => inRange(t.date, range) && (agence === "Toutes" || t.agence === agence) && (module === "Toutes" || t.module === module));
  const body = report.build(scoped, store, range);
  const totals: Cell[] = report.columns.map((_, i) => (i === 0 ? "Total" : report.moneyCols.includes(i) ? body.reduce((a, r) => a + (Number(r[i]) || 0), 0) : ""));
  const periodLabel = `${fDay(range[0])} → ${fDay(range[1])} ${range[1].slice(0, 4)}`;
  const fileBase = `${report.id}_${range[0]}_${range[1]}${agence !== "Toutes" ? `_${agence.replace(/\s+/g, "-")}` : ""}`;

  const fmt = (v: Cell, i: number) => (report.moneyCols.includes(i) && typeof v === "number" ? money(v) : String(v));

  const generate = () => {
    if (format === "PDF") printReport(report.title, periodLabel, agence, module, report.columns, body.map((r) => r.map(fmt)), totals.map(fmt));
    else downloadCsv(`${fileBase}.csv`, report.columns, [...body, totals]);
    setHistory((h) => [{ id: String(Date.now()), report: report.title, period: periodLabel, format, at: new Date().toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }), rows: body.length }, ...h]);
    flash(format === "PDF" ? "Aperçu d'impression ouvert — enregistrez en PDF depuis la boîte d'impression" : `${fileBase}.csv téléchargé (s'ouvre dans Excel)`);
  };

  const sel = "w-full h-9 px-2.5 border border-[#e7dccd] rounded-lg bg-white text-xs";
  const label = "block text-[11px] font-semibold text-gray-600 mb-1";

  return (
    <FinPage title="États & éditions" subtitle="Tirez les états financiers et les éditions de transactions sur la période de votre choix">
      <div className="grid lg:grid-cols-[300px_1fr] gap-4">
        <div className="space-y-2">
          {REPORTS.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setReportId(r.id)}
              className={classNames("w-full text-left p-3 rounded-xl border flex gap-3", r.id === reportId ? "border-[#92400e] bg-[#fdf6ec]" : "border-[#efe7dc] bg-white hover:border-[#e7c9a0]")}
            >
              <DocumentTextIcon className={classNames("w-5 h-5 shrink-0 mt-0.5", r.id === reportId ? "text-[#92400e]" : "text-gray-400")} />
              <span>
                <span className="block text-xs font-bold">{r.title}</span>
                <span className="block text-[11px] text-gray-500 mt-0.5 leading-snug">{r.desc}</span>
              </span>
            </button>
          ))}
        </div>

        <div className="space-y-4 min-w-0">
          <Card title={report.title}>
            <div className="px-4 pb-4 grid grid-cols-2 md:grid-cols-4 gap-3 items-end text-xs">
              <div className="col-span-2">
                <span className={label}>Période</span>
                <PeriodPicker value={period} onChange={setPeriod} from={from} to={to} onFrom={setFrom} onTo={setTo} />
              </div>
              <label className="block">
                <span className={label}>Agence</span>
                <select value={agence} onChange={(e) => setAgence(e.target.value)} className={sel}>
                  <option>Toutes</option>
                  {AGENCES.map((a) => (
                    <option key={a}>{a}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className={label}>Activité</span>
                <select value={module} onChange={(e) => setModule(e.target.value)} className={sel}>
                  <option>Toutes</option>
                  {MODULES.map((m) => (
                    <option key={m}>{m}</option>
                  ))}
                </select>
              </label>
              <div className="col-span-2 md:col-span-3">
                <span className={label}>Format</span>
                <div className="flex gap-2">
                  {(["PDF", "Excel", "CSV"] as const).map((f) => (
                    <button key={f} type="button" onClick={() => setFormat(f)} className={classNames("h-9 px-4 rounded-lg border font-semibold", format === f ? "border-[#92400e] bg-[#fdf6ec] text-[#78350f]" : "border-[#e7dccd] bg-white text-gray-600")}>
                      {f}
                    </button>
                  ))}
                </div>
              </div>
              <button type="button" onClick={generate} disabled={!body.length} className={classNames(btnPrimary, "h-10")}>
                Générer l'édition
              </button>
            </div>
          </Card>

          <Card title="Aperçu" right={<span className="text-[11px] text-gray-500">{body.length} ligne{body.length > 1 ? "s" : ""} · {PERIOD_LABELS[period]} ({periodLabel})</span>}>
            <div className="overflow-x-auto max-h-[420px]">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-[#fbf6ef]">
                  <tr>
                    {report.columns.map((c, i) => (
                      <th key={c} className={classNames("px-4 py-2.5 text-[10.5px] font-bold uppercase tracking-wider text-[#8a7b6b] whitespace-nowrap", report.moneyCols.includes(i) || i > 0 ? "text-right" : "text-left")}>
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {body.slice(0, 60).map((r, j) => (
                    <tr key={j} className="border-t border-[#f5efe6]">
                      {r.map((v, i) => (
                        <td key={i} className={classNames("px-4 py-2 whitespace-nowrap", i > 0 && "text-right", typeof v === "number" && v < 0 && "text-rose-700")}>
                          {fmt(v, i)}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {body.length > 0 && report.moneyCols.length > 0 && (
                    <tr className="border-t-2 border-[#e7dccd] bg-[#fdf8f1] font-bold">
                      {totals.map((v, i) => (
                        <td key={i} className={classNames("px-4 py-2.5 whitespace-nowrap", i > 0 && "text-right")}>
                          {fmt(v, i)}
                        </td>
                      ))}
                    </tr>
                  )}
                </tbody>
              </table>
              {body.length === 0 && <div className="p-8 text-center text-xs text-gray-500">Aucune donnée pour cette période et ces filtres.</div>}
              {body.length > 60 && <div className="px-4 py-2 text-[11px] text-gray-500">60 premières lignes affichées — l'édition contient les {body.length} lignes.</div>}
            </div>
          </Card>

          <Card title="Éditions générées">
            {history.length === 0 ? (
              <div className="px-4 pb-4 text-xs text-gray-500">Aucune édition générée pendant cette session.</div>
            ) : (
              <div className="px-2 pb-2">
                {history.map((h) => (
                  <div key={h.id} className="flex items-center gap-3 px-2 py-2 border-t border-[#f5efe6] text-xs">
                    <span className="px-1.5 py-0.5 rounded bg-[#fdf6ec] text-[#92400e] font-bold text-[10px]">{h.format}</span>
                    <span className="flex-1 font-semibold">{h.report}</span>
                    <span className="text-gray-500">{h.period}</span>
                    <span className="text-gray-500">{h.rows} lignes</span>
                    <span className="text-gray-400">{h.at}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
      <Toast msg={toast} />
    </FinPage>
  );
}

// Opens a print-ready page (the browser's print dialog can save it as PDF).
function printReport(title: string, period: string, agence: string, module: string, columns: string[], rows: string[][], totals: string[]) {
  const w = window.open("", "_blank", "width=900,height=700");
  if (!w) return;
  const esc = (s: string) => s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]!));
  const th = columns.map((c, i) => `<th style="text-align:${i ? "right" : "left"}">${esc(c)}</th>`).join("");
  const tr = (r: string[], bold = false) => `<tr${bold ? ' style="font-weight:700;border-top:2px solid #333"' : ""}>${r.map((c, i) => `<td style="text-align:${i ? "right" : "left"}">${esc(c)}</td>`).join("")}</tr>`;
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>body{font-family:Arial,sans-serif;font-size:11px;color:#1f1b2e;margin:24px}h1{font-size:18px;margin:0}p{color:#555;margin:4px 0 16px}
table{width:100%;border-collapse:collapse}th,td{padding:5px 6px;border-bottom:1px solid #ddd;white-space:nowrap}th{background:#f6efe5;font-size:10px;text-transform:uppercase}</style>
</head><body><h1>${esc(title)}</h1><p>Période : ${esc(period)} · Agence : ${esc(agence)} · Activité : ${esc(module)} · Édité le ${new Date().toLocaleString("fr-FR")}</p>
<table><thead><tr>${th}</tr></thead><tbody>${rows.map((r) => tr(r)).join("")}${tr(totals, true)}</tbody></table></body></html>`);
  w.document.close();
  w.focus();
  w.print();
}

